import {h, Component} from 'preact'
import axios, { AxiosResponse } from 'axios'
import globals from '../../utils/globals'
import InputGroup from '../../control-components/ui-components/InputGroup'
import ButtonControl from '../button-control'
import Logo from '../assets/logo'
import Icon from "../assets";

interface Request {
  endpoint: string,
  fields: Array<string>,
  onRequestSucceeded: Function,
  onRequestFailed: Function
}

type Credentials = {
  email: string,
  username: string,
  login: string,
  password: string,
  remember: boolean,
  roleId?: number,
  [field: string]: any
}

type NewMemberRegistration = {
  inviterUsername: string,
  inviteeEmail: string,
  inviteeRole: number
}

interface Props {
  isStaffLoginEnabled?: boolean,
  customerName?: string,
  newMemberRegistration?: NewMemberRegistration,
  invalidRegistration?: boolean
}

interface State {
  credentials: Credentials,
  formErrors: {
    [item: string]: any
  },
  backendError: JSX.Element | null,
  isLoading: boolean
}

const REQUIRED_FIELDS: Array<string> = ['username', 'login', 'password']
const FORGOT_PASSWORD_URL: string = `${globals('communityUrl')}/member/forgotPassword`
export const LOGIN_ENDPOINT: string = '/member/login'
export const REGISTER_ENDPOINT: string = '/member/register'
const STAFF_LOGIN_COMMUNITY_URL: string = `https://sso.api.insided.com/auth/insidedgodmode?customer=godmode&return_url=${globals('communityUrl')}/ssoproxy/return`
const STAFF_LOGIN_CONTROL_URL: string = `https://sso.api.insided.com/auth/insidedgodmode?customer=godmode&return_url=${globals('controlUrl')}/ssoproxy/return`

const DEFAULT_BACKEND_ERROR_MESSAGE: JSX.Element = <div>Something went wrong, please try again later</div>
const INVALID_CREDENTIALS_BACKEND_ERROR_MESSAGE: JSX.Element = <div>The email address/username or password you entered is incorrect. If you need to, you can <a href={FORGOT_PASSWORD_URL} target='__blank'>reset your password</a>.</div>

const ERROR_MESSAGES: { [key: string] : any} = {
  username: 'Please enter your username',
  login: 'Please enter your email / username',
  password: 'Please enter your password',
}

const InvalidCredentialsAlert = ({ error } : { error: JSX.Element}) =>
    <div class='invalid-credentials-alert'>
      <Icon icon='forbidden' color='#E94C5C'/>
      { error }
    </div>

export default class LoginControl extends Component<Props, State> {
  private RegisterRequest: Request = {
    endpoint: REGISTER_ENDPOINT,
    fields: ['email', 'username', 'password'],
    onRequestSucceeded: this.onRegisterSucceced,
    onRequestFailed: this.onRegisterFailed.bind(this)
  }

  private LoginRequest: Request = {
    endpoint: LOGIN_ENDPOINT,
    fields: ['login', 'password'],
    onRequestSucceeded: this.onLoginSucceced,
    onRequestFailed: this.onLoginFailed.bind(this)
  }

  constructor () {
    super()
    this.state = {
      credentials: this.getDefaultCredentials(),
      formErrors: {},
      backendError: null,
      isLoading: false
    }

    this.onInputChange = this.onInputChange.bind(this)
    this.onSubmit = this.onSubmit.bind(this)
  }

  getDefaultCredentials (): Credentials {
    return {
      email: '',
      username: '',
      login: '',
      password: '',
      remember: false,
    }
  }

  componentDidMount () {
    if(!this.props.newMemberRegistration) return

    const { newMemberRegistration: { inviteeEmail, inviteeRole } } = this.props
    const inviteeUsername = inviteeEmail.split('@')[0]

    this.setState({ credentials:
      Object.assign(this.state.credentials, { email: inviteeEmail, username: inviteeUsername, roleId: inviteeRole })
    })
  }

  onInputChange (e: ChangeEvent): void {
    const { target } = e
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const name = target.name;

    const { credentials, formErrors } = this.state

    if(REQUIRED_FIELDS.includes(name)) {
      if (typeof value !== "boolean") {
        const inputIsEmpty = !value.trim()
        if(inputIsEmpty) {
          this.setState({
            credentials: {...credentials, ...{[name]: value}},
            formErrors: {...formErrors, ...{[name]: ERROR_MESSAGES[name]}},
          })
          return
        }
      }
    }

    delete formErrors![name]

    this.setState({
      credentials: {...credentials, ...{[name]: value}},
      formErrors: formErrors
    })
  }

  validateForm(fields: {}): boolean {
    let errors = {}
    const { credentials } = this.state

    Object.keys(fields).forEach(item => {
      const formField = credentials[item]
      const value = formField.trim()
      if (!value) Object.assign(errors, {[item]: ERROR_MESSAGES[item]})
    });

    if (Object.keys(errors).length > 0) {
      this.setState({formErrors: errors})
      return false
    }

    return true
  }

  getValidationFields(fields: string[]) {
    const validationFields = {}
    const { credentials } = this.state
    fields.forEach(field => {
      Object.assign(validationFields, { [field]: credentials[field] })
    })

    return validationFields
  }

  onLoginSucceced (response: AxiosResponse) {
    const { redirect } = response.data
    window.location.replace(redirect)
    window.sessionStorage.setItem('UserLoggedInControl', String(true))
  }

  onRegisterSucceced (response: AxiosResponse) {
    const { redirect } = response.data
    window.location.replace(redirect)
  }

  onLoginFailed (errorResponse: AxiosResponse) {
    const { status } = errorResponse

    if(status === 422) {
      this.setState({ credentials: this.getDefaultCredentials(), backendError: INVALID_CREDENTIALS_BACKEND_ERROR_MESSAGE })
      return
    }

    this.setState({ credentials: this.getDefaultCredentials(), backendError: DEFAULT_BACKEND_ERROR_MESSAGE})
  }

  onRegisterFailed (errorResponse: AxiosResponse) {
    const { data, status } = errorResponse

    if(status === 422) {
      data.forEach((error: { name: string; description: string }) => {
        const { name, description } = error
        this.setState({
          credentials: Object.assign(this.state.credentials, {password: ''}),
          formErrors: {...this.state.formErrors, ...{[name]: description}},
          backendError: null
        })
      })
      return
    }

    this.setState({
      credentials: Object.assign(this.state.credentials, {password: ''}),
      backendError: DEFAULT_BACKEND_ERROR_MESSAGE
    })
  }

  async onSubmit (e: Event, request: Request): Promise<void> {
    e.preventDefault()

    const validationFields = this.getValidationFields(request.fields)

    if (this.validateForm(validationFields)) {
      const {credentials} = this.state
      try {
        this.setState({ isLoading: true })
        const response = await axios.post(request.endpoint, credentials)
        request.onRequestSucceeded(response)
      } catch (e) {
        const response =  e.response
        request.onRequestFailed(response)
        this.setState({ isLoading: false })
      }
    }
  }

  render ({
    isStaffLoginEnabled,
    customerName,
    newMemberRegistration,
    invalidRegistration } : Props,
    { credentials: {
      email,
      username,
      login,
      password,
      remember
    },
    formErrors,
    backendError,
    isLoading } : State) {

    if(invalidRegistration) {
      return <Wrapper>
        <h2 className='control-login-title'>Invite is no longer active</h2>
        <h3 className='control-login-subtitle'>This invite has expired, or the sender has cancelled it. Please check with the person who invited you to get a new invite.</h3>
        <a href='https://insided.com' target='_blank'>
          <ButtonControl class='register-button' variant='brand' title='Learn more about inSided' fullWidth/>
        </a>
      </Wrapper>
    }

    if (newMemberRegistration) {
      const { inviterUsername } = newMemberRegistration
      return <Wrapper onSubmit={(e: Event) => this.onSubmit(e, this.RegisterRequest)}>
        <h2 className='control-login-title'>{inviterUsername} has invited you to team: {customerName}</h2>
        <h3 className='control-login-subtitle'>You’re almost there. Last step: create an account.</h3>
        {backendError && <InvalidCredentialsAlert error={backendError}/>}
        <div className='control-login-box'>
          <div className='login-input-group'>
            <InputGroup name='email' title='Email' type='email' value={email}
                        error={formErrors['email']} disabled />
            <InputGroup name='username' title='Username' type='text' value={username}
                        onChangeCallback={this.onInputChange} error={formErrors['username']} disabled={isLoading} />
            <InputGroup name='password' title='Password' type='password' value={password}
                        onChangeCallback={this.onInputChange} error={formErrors['password']} disabled={isLoading} />
          </div>
          <ButtonControl isLoading={isLoading} type='submit' class='register-button' variant='brand' title='Create account' fullWidth/>
        </div>
      </Wrapper>
    }

    return <Wrapper onSubmit={(e: Event) => this.onSubmit(e, this.LoginRequest)}>
      <h2 className='control-login-title'>Log in to {customerName}</h2>
      {backendError && <InvalidCredentialsAlert error={backendError}/>}
      <div className='control-login-box'>
        <div className='login-input-group'>
          <InputGroup name='login' title='Email / Username' type='text' value={login}
                      onChangeCallback={this.onInputChange} error={formErrors['login']} disabled={isLoading}/>
          <InputGroup name='password' title='Password' type='password' value={password}
                      onChangeCallback={this.onInputChange} error={formErrors['password']} disabled={isLoading}/>
        </div>
        <div className='login-meta'>
          <InputGroup inline name='remember' title='Remember me' type='checkbox' checked={remember}
                      onChangeCheckbox={this.onInputChange}/>
          <a href={FORGOT_PASSWORD_URL} target='__blank' className='login-meta-forgot-password'>Forgot password?</a>
        </div>
        <ButtonControl isLoading={isLoading} type='submit' class='login-button' variant='brand' title='Log in' fullWidth/>
        {isStaffLoginEnabled && <div className='staff-login-buttons'>
          <ButtonControl onClick={() => window.location.replace(STAFF_LOGIN_CONTROL_URL)} class='login-button'
                         variant='purple' title='Staff log in - control'/>
          <ButtonControl onClick={() => window.location.replace(STAFF_LOGIN_COMMUNITY_URL)} class='login-button'
                         variant='purple' title='Staff log in - community'/>
        </div>}
      </div>
    </Wrapper>
  }
}

const Wrapper = ({ onSubmit, children } : { onSubmit?: (e: Event) => void, children: any }) => {
  return <div className='control-login'>
    <Logo width='164' height='48' customClass='control-login-logo'/>
    <img class='control-login-side-image' src={`${globals('basePath')}images/control-login-side-image.png`} />
    <form className='control-login-wrapper' onSubmit={onSubmit}>
      {children}
    </form>
  </div>
}
